import { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';
import { PdfViewer } from '../../components/PdfViewer';
import { TearLine } from '../../components/TearLine';

/**
 * Inline template preview (Pack B Item 4). Fetches the server-rendered
 * sample-data PDF through the authenticated API client (so the bearer token is
 * attached), hands it to react-pdf via a same-origin blob URL, and shows a
 * loading skeleton then a graceful "Preview unavailable" on failure — it never
 * blocks the rest of the page.
 */
export function TemplatePreview({ templateId }: { templateId: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    setState('loading');
    setBlobUrl(null);
    apiClient
      .get(`/api/v1/document-templates/${templateId}/preview`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data as Blob);
        setBlobUrl(url);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [templateId]);

  if (state === 'error') {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
        Preview unavailable.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-surface">
      <TearLine />
      {state === 'loading' || !blobUrl ? (
        <div className="h-64 animate-pulse bg-slate-100" aria-label="Loading preview" />
      ) : (
        <PdfViewer url={blobUrl} />
      )}
    </div>
  );
}
